<template>
  <div class="OfferCard" :class="cptdCSSOfferCard">
    <div v-if="!!tag" class="tag" :class="tag.type">
      {{ tag.label }}
    </div>
    <div class="card">
      <div class="partner-wrapper">
        <PartnerLogo
          v-if="cptdPartner && !cptdIsProductCard"
          class="partner-logo"
          :partner-id="cptdPartner"
          @click="$emit('partner-info-click')"
        />
        <div v-else class="artwork">
          <BaseIcon v-if="cptdProductIcon" :name="cptdProductIcon" />
        </div>
        <div class="partner-data">
          <p
            v-if="!cptdIsProductCard"
            class="partner-name"
            @click="$emit('partner-info-click')"
          >
            {{ cptdPartnerName }}
          </p>
          <p v-else class="product-name">{{ cptdProductName }}</p>
          <slot v-if="!cptdIsProductCard" name="product-badge" />
        </div>
      </div>
      <div class="data">
        <slot v-if="offer" name="content">
          <div class="wrapper">
            <div class="values">
              <p class="title">Valor</p>
              <p class="value amount">{{ cptdOfferAmountBrl }}</p>
            </div>
            <slot name="amount-info" />
          </div>
          <div class="wrapper">
            <div class="values">
              <p class="title">Parcelas</p>
              <p class="value installment">
                {{ offer.duration }}x de {{ cptdInstallmentAmountBrl }}
              </p>
            </div>
            <slot name="duration-info" />
          </div>
          <div class="wrapper">
            <div class="values">
              <p class="title">Juros ao mês</p>
              <p class="value interest-rate">{{ cptdMonthlyInterestRate }}%</p>
            </div>
            <slot name="interest-rate-info" />
          </div>
        </slot>
      </div>
      <div class="actions">
        <div class="buttons">
          <slot name="actions">
            <Button type="primary" @click="onSelected">Solicitar</Button>
          </slot>
        </div>
        <div v-if="displayDisclaimer" class="disclaimer">
          <slot name="disclaimer">{{ cptdDisclaimer }}</slot>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { PartnersName } from '@/helpers/partners/common'
import { parseToBRL } from '@/helpers/currency'
import { formatDateToNumericDate, localDate } from '@/helpers/date'
import Button from '@/aasgard/components/buttons/Button.vue'
import BaseIcon from '@/aasgard/components/base/BaseIcon.vue'
import { defineAsyncComponent } from 'vue'
const PartnerLogo = defineAsyncComponent(
  () => import('@/components/appForm/loanResults/PartnerLogo.vue'),
)
export default {
  name: 'OfferCard',
  components: {
    BaseIcon,
    PartnerLogo,
    Button,
  },
  props: {
    offer: { type: Object, default: () => ({}) },
    partner: { type: String, default: undefined },
    tag: { type: Object, default: undefined },
    displayDisclaimer: { type: Boolean, default: true },
  },
  emits: ['partner-info-click', 'selected'],
  computed: {
    cptdDisclaimer() {
      if (this.cptdOfferExpirationDate) return this.cptdOfferExpirationDate

      return 'Oferta sujeita à análise de crédito.'
    },
    cptdCSSOfferCard() {
      return {
        'has-tag': Object.keys(this.tag || {}).length,
      }
    },
    cptdPartner() {
      return this.partner ?? this.offer?.partner ?? ''
    },
    cptdIsProductCard() {
      return [
        'NO_COLLATERAL',
        'AUTO_EQUITY',
        'MOBILE_EQUITY',
        'HOME_EQUITY',
        'CREDIT_CARD_EQUITY',
        'PAYROLL',
        'FGTS',
      ].includes(this.cptdPartner)
    },
    cptdProductName() {
      if (!this.cptdIsProductCard) return ''
      const productNameMap = {
        NO_COLLATERAL: 'Empréstimo pessoal',
        AUTO_EQUITY: 'Empréstimo com veículo em garantia',
        MOBILE_EQUITY: 'Empréstimo com celular em garantia',
        HOME_EQUITY: 'Empréstimo com imóvel em garantia',
        CREDIT_CARD_EQUITY: 'Empréstimo no cartão',
        PAYROLL: 'Empréstimo consignado',
        FGTS: 'Empréstimo saque FGTS',
      }

      return productNameMap[this.cptdPartner] ?? ''
    },
    cptdProductIcon() {
      if (!this.cptdIsProductCard) return ''
      const productIconsMap = {
        NO_COLLATERAL: 'hand-money-mono',
        AUTO_EQUITY: 'car-money-mono',
        MOBILE_EQUITY: 'smartphone-mono',
        HOME_EQUITY: 'home-money-mono',
        CREDIT_CARD_EQUITY: 'credit-card-mono',
        PAYROLL: 'hand-money-mono',
        FGTS: 'hand-money-mono',
      }

      return productIconsMap[this.cptdPartner] ?? ''
    },
    cptdOfferAmountBrl() {
      return this.mxParseToBRL(this?.offer?.amount ?? 0)
    },
    cptdMonthlyInterestRate() {
      return (
        this.offer?.display_interest_rate_monthly ??
        this.offer?.interest_rate_monthly ??
        0
      )
        ?.toFixed(2)
        ?.replace('.', ',')
    },
    cptdInstallmentAmountBrl() {
      return this.mxParseToBRL(this.offer?.installment_amount ?? 0)
    },
    cptdPartnerName() {
      return PartnersName[this.cptdPartner ?? ''] ?? ''
    },
    cptdOfferExpirationDate() {
      if (!this.offer?.expiration_date_time) return ''
      try {
        return `Válida até ${formatDateToNumericDate(
          localDate(this.offer?.expiration_date_time),
          'dd/mm/yyyy as hh:mm',
        )}`
      } catch (e) {
        return ''
      }
    },
  },
  methods: {
    onSelected() {
      this.$emit('selected', this.offer)
    },
    mxParseToBRL(value) {
      return parseToBRL(value)
    },
  },
}
</script>
<style lang="scss">
.OfferCard {
  position: relative;

  &.has-tag {
    padding-top: 32px;
  }

  & > .tag {
    @include caption;
    color: $color-black-high;
    padding: 8px 16px;
    width: 100%;
    height: 56px;
    border-radius: 24px 24px 0 0;
    text-align: center;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
    white-space: nowrap;

    &.neutral,
    &.NEUTRAL {
      background-color: $color-neutral-dark;
    }
    &.positive,
    &.POSITIVE {
      background-color: $color-feedback-positive;
      color: $color-white-high;

      & + .card {
        border: 4px solid $color-feedback-positive;
      }
    }
    &.positive-light,
    &.POSITIVE-LIGHT {
      background-color: $color-feedback-positive-light;
    }
    &.error,
    &.ERROR {
      background-color: $color-feedback-negative-light;
    }
    &.warning,
    &.WARNING {
      background-color: $color-feedback-warning;
    }
    &.warning-light,
    &.WARNING-LIGHT {
      background-color: $color-feedback-warning-light;
    }
  }
  .card {
    width: 100%;
    display: flex;
    flex-direction: column;
    background: $color-white-high;
    border: 1px solid $color-neutral-dark;
    box-shadow: 0 2px 2px $color-neutral-dark;
    border-radius: 24px;
    padding: 0;
    position: relative;
    z-index: 0;

    & > .partner-wrapper {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 24px 24px 16px 24px;

      & > .partner-logo,
      & > .artwork {
        width: 56px;
        min-width: 56px;
        height: 56px;
        min-height: 56px;
        border-radius: 50%;
      }

      & > .partner-logo {
        cursor: pointer;
      }

      & > .artwork {
        background-color: $color-neutral-light;
        display: flex;
        justify-content: center;
        align-items: center;

        & > .BaseIcon {
          @include setSVGMonoColor($color-black-high);
        }
      }

      & > .partner-data {
        padding: 0 16px;

        & > .partner-name,
        & > .product-name {
          @include subtitle-1;
          color: $color-black-high;
          margin: 0 0 8px 0;
          cursor: pointer;
        }

        & > .partner-name {
          cursor: pointer;
        }
      }
    }

    & > .data {
      padding: 0 24px 16px 24px;
      overflow: hidden;
      position: relative;

      & > .wrapper {
        margin-bottom: 8px;
        display: flex;
        justify-content: space-between;
        align-items: center;

        &:last-child {
          margin: 0;
        }

        & > .BaseLink {
          @include subtitle-2;
          text-decoration: none;
        }
      }

      .title {
        @include body-2;
        color: $color-black-medium;
        margin: 0;
      }

      .value {
        @include subtitle-1;
        color: $color-black-high;
        margin: 0 0 8px 0;

        &:last-child {
          margin: 0;
        }
      }
    }

    & > .actions {
      padding: 0 24px 24px 24px;
      margin-top: auto;

      & > .buttons {
        display: flex;
        align-items: center;
        gap: 8px;

        .Button {
          width: 100%;
        }
      }

      & > .disclaimer {
        @include caption;
        text-align: center;
        color: $color-black-medium;
        margin: 16px 0 0 0;
      }
    }
  }
}
</style>
